// Typed API client — access token injected per request via getAccessToken()
export type GetAccessTokenFn = () => Promise<string | null>;

let _getAccessToken: GetAccessTokenFn = async () => null;

export function configureApiClient(fn: GetAccessTokenFn) {
  _getAccessToken = fn;
}

async function apiFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const token = await _getAccessToken();
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(input, { ...init, headers, credentials: 'include' });

  if (res.status === 401) {
    // Token expired — clear it and let the caller handle the error
    _getAccessToken = async () => null;
  }

  return res;
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await apiFetch(`/api${path}`);
  if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
  return res.json() as Promise<T>;
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method: 'POST', headers: { 'Content-Type': 'application/json' } };
  if (body !== undefined) init.body = JSON.stringify(body);
  const res = await apiFetch(`/api${path}`, init);
  if (!res.ok) {
    const data = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(data.error ?? `POST ${path} failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export async function apiPut(path: string, body?: unknown): Promise<void> {
  const init: RequestInit = { method: 'PUT', headers: { 'Content-Type': 'application/json' } };
  if (body !== undefined) init.body = JSON.stringify(body);
  const res = await apiFetch(`/api${path}`, init);
  if (!res.ok) throw new Error(`PUT ${path} failed: ${res.status}`);
}

export async function apiDelete(path: string): Promise<void> {
  const res = await apiFetch(`/api${path}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`DELETE ${path} failed: ${res.status}`);
}
